// Brand ramp centered on the accent #0078D4 (app.md §7). Setting it as the
// brand ramp's base lets primary actions / selection / highlights read from
// Fluent brand tokens instead of hard-coded hex. Everything else stays neutral.
import { createLightTheme, makeStyles, shorthands, tokens, type BrandVariants, type Theme } from '@fluentui/react-components'

const brand: BrandVariants = {
  10: '#020405',
  20: '#0E1A29',
  30: '#0E2A47',
  40: '#0B3A63',
  50: '#064A80',
  60: '#005B9E',
  70: '#006CBD',
  80: '#0078D4', // rest / primary
  90: '#2E8CDB',
  100: '#4C9CE2',
  110: '#67ACE8',
  120: '#82BCEE',
  130: '#9DCBF3',
  140: '#B7DAF7',
  150: '#D2E9FB',
  160: '#EBF4FE',
}

export const appTheme: Theme = createLightTheme(brand)

/**
 * THE EXPANDED QUEUE CARD'S HEADER FILL, the pale blue a queue row takes when it
 * opens (`InvoiceQueue.tsx`).
 */
export const EXPANDED_HEADER_FILL = '#F5F9FD'

/**
 * THE LINE TABLE'S LEFTMOST MARKS, ONE LOOK (owner, 2026-10-23 — D-407: "The
 * marks are not quite aligned, and I would like them to be of equal weight").
 * The header's Expand All / Collapse All, a group row's expand arrow and a
 * line's drag handle all take this class: the same 24×24 box at the left of
 * the expand column, the same 16px icon, and one neutral colour, at rest and
 * on hover. `InvoiceLineItemsTable.tsx` and `LineGroupRow.tsx` both read it.
 *
 * `markOutlined` gives a group row's arrow a blue border (owner, 2026-10-28 —
 * D-424; white on a blue fill, `markFilled`, from D-419 until then).
 * `markBrand` turns a TOP-LEVEL line's drag handle blue, unfilled; a line
 * inside a group keeps the neutral handle (D-408). Both handles take
 * `markHeavy`, which thickens the six dots (owner, 2026-10-28: "go back to the
 * non-filled blue- and gray-colored drag icons for the actual items ... add a
 * bit of extra weight to the drag icons"). From 2026-10-27 to 2026-10-28 the
 * top-level handle was filled too. This replaced the row fills of D-405 and
 * D-407.
 */
export const LINE_MARK_BOX = 24
export const LINE_MARK_ICON = 16
export const useLineMarkStyles = makeStyles({
  mark: {
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'middle',
    flexShrink: 0,
    width: `${LINE_MARK_BOX}px`,
    minWidth: `${LINE_MARK_BOX}px`,
    maxWidth: `${LINE_MARK_BOX}px`,
    height: `${LINE_MARK_BOX}px`,
    minHeight: `${LINE_MARK_BOX}px`,
    padding: 0,
    margin: 0,
    fontSize: `${LINE_MARK_ICON}px`,
    color: tokens.colorNeutralForeground2,
    ':hover': { color: tokens.colorNeutralForeground2 },
    ':hover:active': { color: tokens.colorNeutralForeground2 },
    '& .fui-Button__icon': {
      width: `${LINE_MARK_ICON}px`,
      height: `${LINE_MARK_ICON}px`,
      fontSize: `${LINE_MARK_ICON}px`,
      color: tokens.colorNeutralForeground2,
    },
    '& svg': { width: `${LINE_MARK_ICON}px`, height: `${LINE_MARK_ICON}px` },
  },
  // A GROUP ROW'S ARROW (owner, 2026-10-28 — D-424: "the blue fills behind the
  // chevrons are a bit much ... let's try a blue border (which turns red or
  // yellow when needed)"): a blue icon in a blue 1px border. From D-419 until
  // then it was white on a blue fill.
  markOutlined: {
    color: tokens.colorBrandForeground1,
    outline: `1px solid ${tokens.colorBrandStroke1}`,
    outlineOffset: '-1px',
    borderRadius: tokens.borderRadiusMedium,
    ':hover': { color: tokens.colorBrandForeground1 },
    ':hover:active': { color: tokens.colorBrandForeground1 },
    '& .fui-Button__icon': { color: tokens.colorBrandForeground1 },
  },
  // Its border yellow while a line in the group carries an unresolved flag
  // (D-417), red while the group blocks (a field of its own is red, D-406);
  // red wins, as on every field (D-248).
  markFlagged: {
    outline: `1px solid ${tokens.colorPaletteMarigoldBorder2}`,
  },
  markRequired: {
    outline: `1px solid ${tokens.colorPaletteRedBorder2}`,
  },
  markBrand: {
    color: tokens.colorBrandForeground1,
    ':hover': { color: tokens.colorBrandForeground1 },
    ':hover:active': { color: tokens.colorBrandForeground1 },
    '& .fui-Button__icon': { color: tokens.colorBrandForeground1 },
  },
  // The drag handle's dots, outlined in their own colour so they read heavier.
  markHeavy: {
    '& svg path': { stroke: 'currentColor', strokeWidth: '0.75px' },
  },
  // A group with nothing in it (D-423): disabled, a gray icon in a gray border.
  markDisabled: {
    color: tokens.colorNeutralForegroundDisabled,
    outline: `1px solid ${tokens.colorNeutralStrokeDisabled}`,
    outlineOffset: '-1px',
    borderRadius: tokens.borderRadiusMedium,
    '& .fui-Button__icon': { color: tokens.colorNeutralForegroundDisabled },
  },
})

/**
 * One colour per DASHBOARD SECTION (owner, 2026-09-15: "can you use a different
 * color for the bars per section?").
 *
 * The colour identifies the SECTION, not the measure — all four panels under
 * *Per Vendor* are teal, all four under *Per Company* purple, and so on. That is
 * the opposite of the first cut, which coloured by measure (counts blue, days
 * orange, denials red) and so repeated the same three colours down the page,
 * telling a reader nothing about where they were.
 *
 * Fluent's accessible palette, all five at or above 4.5:1 on the panel's white
 * ground, and all five distinguishable from each other in the common forms of
 * colour blindness — no red/green pair carries meaning here.
 *
 * The age hues (`AGE_RAMP` below) still mean what they mean: they encode AGE,
 * and a bar coloured by its section must never borrow them.
 */
export const SECTION_COLORS = {
  step: '#0078D4',
  vendor: '#038387',
  company: '#8764B8',
  spend: '#498205',
  feedback: '#CA5010',
} as const

/**
 * The AGE RAMP as solid fills, youngest to oldest — what the KPI block's
 * outstanding-by-age bar is drawn in (owner, 2026-09-15).
 *
 * The SAME four hues `AGE_PILL_COLORS` gives the queue cards' age pills, taken
 * from `TINT_PILLS`' `dot` values, so a segment of the bar and the pill on the
 * row it stands for are the same colour. Its youngest band is green, not
 * `transparent`: a solid run drawn transparent would be invisible.
 *
 * Indexed by BAND POSITION, not by a day threshold — the bands come from the
 * API with their own bounds (`OUTSTANDING_AGE_BANDS`), and nothing here may
 * hardcode one.
 *
 * FOUR COLOURS IS A CAP ON THE BAND COUNT, not just a palette. `ageRampFill`
 * stretches these four across however many bands arrive, so five bands would
 * put two of them on one hue — which is exactly what the owner cut on
 * 2026-09-20, when a per-day scale of eight bands rendered as four colours
 * labelled eight ways. The scale is four bands for that reason; adding a fifth
 * means adding a fifth colour here, and the hard part is that it would sit
 * between orange and red, the two already closest together.
 */
export const AGE_RAMP: readonly string[] = ['#107C10', '#F2C744', '#E08A2B', '#D13438']

/** The fill for the band at `index` of `count`. The oldest band is always the
    reddest, however many there are. */
export function ageRampFill(index: number, count: number): string {
  if (count <= 1) return AGE_RAMP[AGE_RAMP.length - 1]!
  // Stretch the ramp across however many bands arrived, so the first is always
  // the greenest and the last always the reddest.
  const position = Math.round((index / (count - 1)) * (AGE_RAMP.length - 1))
  return AGE_RAMP[Math.min(Math.max(position, 0), AGE_RAMP.length - 1)]!
}

// Tinted dot-pill palette (v1.20 card queue): pale background, darker text,
// solid dot. Green/red double as the semantic good/bad pair (reconcile chip,
// flags pill).
export interface PillColors {
  fg: string
  bg: string
  dot: string
}

export const TINT_PILLS: Record<'green' | 'yellow' | 'orange' | 'red' | 'blue', PillColors> = {
  green: { fg: '#0E700E', bg: '#EBF5EB', dot: '#107C10' },
  yellow: { fg: '#8A6A0C', bg: '#FBF5DC', dot: '#F2C744' },
  orange: { fg: '#9C5B12', bg: '#FDF1E4', dot: '#E08A2B' },
  red: { fg: '#B02A2E', bg: '#FBEDED', dot: '#D13438' },
  // Informational (the manual-entry marker) — brand-tinted, not semantic.
  blue: { fg: '#0F548C', bg: '#EBF3FC', dot: '#0078D4' },
}

// Age band → pill colors. The cards carry no colored border, so the pill alone
// reads the band; the scale runs green (fresh) → red (7+ days).
export const AGE_PILL_COLORS: Record<string, PillColors> = {
  neutral: TINT_PILLS.green,
  yellow: TINT_PILLS.yellow,
  orange: TINT_PILLS.orange,
  red: TINT_PILLS.red,
}

// The bolded label sitting between the reconcile pill (or the "View With Tax &
// Shipping Distributed" toggle + pill) and the summary figure it names — "Invoice Subtotal:"
// / "Invoice Total:" (owner, 2026-08-14). One shared class so the hand-rolled
// copies — `InvoiceLineItemsTable`'s `ReconcileFooter`, the editable form's own
// footer and `GLSummary`'s grand-total row — carry the same weight rather
// than drifting. Those rows are already `fontWeightSemibold` at the container
// level, so this steps up to `fontWeightBold` to still read as distinct emphasis
// next to the figure.
//
// `amount` IS THE SPACING RULE, and it exists because the two blocks did not
// share one (owner, 2026-08-14: "the spacing for the labels on these calculated
// rows differs between sections… align them, using the spacing currently shown
// for Accounts Summary" — the rollup now headed GL Summary). The Line Items
// Summary's footer was a plain flex row
// with a 12px gap, which put its label hard against a figure of whatever width;
// the GL Summary's grand total sits in a table cell, so its label ends a
// fixed distance from the figure. This class reproduces THE TABLE CELL, so the
// flex footers read the GL Summary way.
//
// THE NUMBERS ARE MEASURED, not guessed. `GLSummary`'s amount cell is
// `<TableCell style={{ width: 120 }}>` and Fluent's `size="small"` cell adds 8px
// of padding on each side, so its rendered box is 136px and its TEXT stops 8px
// short of the box's right edge. Reproducing both — a 136px border box with 8px
// of right padding — is what puts the figures on one baseline instead of 8px
// apart, and an 8px flex gap beside it puts the labels on one too. Verified in
// the browser: with a $1,226.99 total in both blocks, each label's right edge
// lands on 1175 and each figure's on 1311.
//
// A footer using this class MUST set its own row `gap` to 8px to match.
const SUMMARY_AMOUNT_WIDTH = 136
const SUMMARY_AMOUNT_PAD = 8
export const useSummaryLabelStyles = makeStyles({
  label: { fontWeight: tokens.fontWeightBold },
  amount: {
    display: 'inline-block',
    boxSizing: 'border-box',
    width: `${SUMMARY_AMOUNT_WIDTH}px`,
    paddingRight: `${SUMMARY_AMOUNT_PAD}px`,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
})

// Right-aligned digits inside a money `Input` (owner item 2, 2026-08-19). Goes
// on the Fluent `input` slot, NEVER `className` — `className` on an `Input`
// styles the root slot (the flex wrapper holding `contentBefore="$"` and the
// border), and `textAlign` there does nothing to the typed digits
// (`app/README.md`'s Fluent v9 gotchas). `tabular-nums` matches the existing
// derived-money cells (`EditableInvoiceForm.tsx`'s `derivedCell`). The spin-
// button suppression is here too because two of the twelve sites
// (`FilterButton.tsx`'s Min/Max total) are `type="number"`: right-aligned
// digits would otherwise sit under the native spin arrows.
export const useMoneyInputStyles = makeStyles({
  moneyText: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { appearance: 'none', margin: 0 },
  },
})

// THE INVOICE SUMMARY'S TWO-COLUMN FIELD GRID, ONE DEFINITION FOR BOTH MODES
// (QA 2026-09-30, C1-09). The editor (`EditableInvoiceForm`) and the read-only
// panel (`InvoiceHeaderPanel`) each had `gridTemplateColumns: '1fr 1fr'`. A
// `1fr` track is `minmax(auto, 1fr)`: it cannot shrink below its widest item's
// min-content, and a Fluent `Input` has one of about 150px, the native input's
// own intrinsic width. At a 953px window the summary column is about 290px, so
// the two tracks pushed the grid past the modal body by about 100px, and the
// right-hand fields (Vendor, External ID, Shipping, Total) were cut off once
// the body clipped sideways overflow. `minmax(0, 1fr)` lets the tracks share
// the column whatever their content, `minWidth: 0` on each field lets it take
// its track's width, and `minWidth: 0` on the Input root makes its minimum
// contribution zero inside the Field's own grid, so the text box shrinks and
// its text scrolls within it instead. A Combobox already clears its 250px floor
// in `EntityPicker`; the selector is here too so no picker in the grid can
// set it again.
//
// THE GAPS ARE THE POP-UP FORM STANDARD'S (D-349): each caller merges this
// after `useDialogFormStyles().grid2`, which spaces the fields 16px down and
// across (they were 12px down until then). Stretch, not the standard's
// bottom alignment, so every box in a row keeps one height.
export const useSummaryGridStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    alignItems: 'stretch',
    '& > *': { minWidth: 0 },
    '& .fui-Input': { minWidth: 0 },
    '& .fui-Combobox': { minWidth: 0 },
  },
})

// Right-aligned digits inside a NON-MONEY numeric `Input` — Quantity (owner item 1, 2026-08-28: *"Can we right-align number
// values, like Quantity? I just want them to look normal sitting next to
// right-aligned dollar values."*).
//
// A SEPARATE CLASS FROM `moneyText`, deliberately, even though the two rules
// are identical today. `moneyText` marks a field as money — `contentBefore="$"`,
// comma grouping, two decimals, `filterMoneyInput`/`money2` on the way in and
// out. None of that applies to a quantity ("1,200 units" is not what anyone
// means — `EditableInvoiceForm.tsx`'s own note on the Quantity cell), so a
// reader finding `moneyText` on a Quantity box would draw the wrong conclusion
// about how the value is handled. Alignment is all these two share.
//
// Same slot rule as `moneyText`: goes on the Fluent `input` slot, NEVER
// `className`. The spin-button suppression is here for the same reason too —
// every caller of this class is `type="number"` while editable, and native spin
// arrows sit exactly where right-aligned digits now end.
export const useNumberInputStyles = makeStyles({
  numText: {
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { appearance: 'none', margin: 0 },
  },
})

// A REQUIRED BOX LEFT EMPTY (owner, 2026-09-21; one rule since D-156 —
// `./required.ts`). The header fields say this with their `Field`'s own
// `validationState`, and `EntityPicker` with its `invalid` prop; a plain
// `Input` in a table cell has neither, so it takes `requiredEmpty`. The line
// icons take `requiredIcon`. Every one is `colorPaletteRedBorder2`, the token
// Fluent's own error state paints, so they all read as one thing.
export const useRequiredStyles = makeStyles({
  requiredEmpty: {
    borderTopColor: tokens.colorPaletteRedBorder2,
    borderRightColor: tokens.colorPaletteRedBorder2,
    borderBottomColor: tokens.colorPaletteRedBorder2,
    borderLeftColor: tokens.colorPaletteRedBorder2,
    // Fluent's hover colour would otherwise repaint the border, so a locked red
    // box flickered under the pointer (owner, 2026-10-27). Two keys, because
    // Fluent uses two: `:hover` on an outline box, and `:hover,:focus-within` on
    // a filled one — every locked box. A class merges over Fluent's only where
    // the selector text is identical, so `:hover` alone left the filled boxes
    // turning clear under the pointer (owner, 2026-10-29 — D-445).
    ':hover': {
      borderTopColor: tokens.colorPaletteRedBorder2,
      borderRightColor: tokens.colorPaletteRedBorder2,
      borderBottomColor: tokens.colorPaletteRedBorder2,
      borderLeftColor: tokens.colorPaletteRedBorder2,
    },
    ':hover,:focus-within': {
      borderTopColor: tokens.colorPaletteRedBorder2,
      borderRightColor: tokens.colorPaletteRedBorder2,
      borderBottomColor: tokens.colorPaletteRedBorder2,
      borderLeftColor: tokens.colorPaletteRedBorder2,
    },
    '::after': { borderBottomColor: tokens.colorPaletteRedBorder2 },
  },
  requiredIcon: {
    border: `1px solid ${tokens.colorPaletteRedBorder2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  // A FIELD TIED TO AN UNRESOLVED FLAG (owner, 2026-09-30 — D-248; which flag
  // ties to which field is `./flagged.ts`). The same weight as the red, on
  // every control type: an `Input` or a picker's root takes it as a
  // `className`. `colorPaletteMarigoldBorder2` (#eaa300 light, #efb839 dark),
  // not the Yellow palette: Yellow's border token is #fde300 in the light
  // theme, which barely shows on a white box. The hover rule is there because
  // Fluent's own hover colour would otherwise repaint the border.
  flagged: {
    borderTopColor: tokens.colorPaletteMarigoldBorder2,
    borderRightColor: tokens.colorPaletteMarigoldBorder2,
    borderBottomColor: tokens.colorPaletteMarigoldBorder2,
    borderLeftColor: tokens.colorPaletteMarigoldBorder2,
    ':hover': {
      borderTopColor: tokens.colorPaletteMarigoldBorder2,
      borderRightColor: tokens.colorPaletteMarigoldBorder2,
      borderBottomColor: tokens.colorPaletteMarigoldBorder2,
      borderLeftColor: tokens.colorPaletteMarigoldBorder2,
    },
    '::after': { borderBottomColor: tokens.colorPaletteMarigoldBorder2 },
    // A filled (locked) box's own key, as on `requiredEmpty` above (D-445).
    ':hover,:focus-within': {
      borderTopColor: tokens.colorPaletteMarigoldBorder2,
      borderRightColor: tokens.colorPaletteMarigoldBorder2,
      borderBottomColor: tokens.colorPaletteMarigoldBorder2,
      borderLeftColor: tokens.colorPaletteMarigoldBorder2,
    },
  },
})

/**
 * The Additional Detail pop-up's panel and its section headings, which the
 * Associated Entities section inside it shares (owner, 2026-10-28 — D-425).
 * The pop-up's own surface, not a gray panel: every value inside is a box, as
 * every other field is.
 */
// Used by `AdditionalDetailPopover.tsx` and `AssociatedEntitiesEditor.tsx`.
export const useLineItemPanelStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '4px 4px',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  head: { display: 'flex', alignItems: 'center', gap: '8px' },
  // THE HEADING, LIKE "SHOW GROUP ROWS" (owner, 2026-10-28 — D-425): that
  // switch's label as the line table's footer draws it — 12px, semibold, the
  // body text colour, in the words' own case.
  headLabel: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  // A sentence about the panel rather than a value in it: an unsaved line, a
  // hold. Quiet, like the Associated Entities editor's "none" line.
  note: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
})

// Green-filled Approve / red-filled Deny (app.md §9 steps 6–8) — shared by the
// queue row buttons, the modal action bar, and the bulk bar. Disabled states
// fall back to Fluent's neutral disabled look so a greyed button still reads
// as unavailable.
export const useDecisionButtonStyles = makeStyles({
  approve: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.borderColor('transparent'),
    '&:hover': { backgroundColor: tokens.colorPaletteGreenForeground1, color: tokens.colorNeutralForegroundOnBrand, ...shorthands.borderColor('transparent') },
    '&:hover:active': { backgroundColor: tokens.colorPaletteGreenForeground2, color: tokens.colorNeutralForegroundOnBrand, ...shorthands.borderColor('transparent') },
    '&:disabled': { backgroundColor: tokens.colorNeutralBackgroundDisabled, color: tokens.colorNeutralForegroundDisabled, ...shorthands.borderColor(tokens.colorNeutralStrokeDisabled) },
    '&:disabled:hover': { backgroundColor: tokens.colorNeutralBackgroundDisabled, color: tokens.colorNeutralForegroundDisabled, ...shorthands.borderColor(tokens.colorNeutralStrokeDisabled) },
  },
  deny: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    ...shorthands.borderColor('transparent'),
    '&:hover': { backgroundColor: tokens.colorPaletteRedForeground1, color: tokens.colorNeutralForegroundOnBrand, ...shorthands.borderColor('transparent') },
    '&:hover:active': { backgroundColor: tokens.colorPaletteRedForeground2, color: tokens.colorNeutralForegroundOnBrand, ...shorthands.borderColor('transparent') },
    '&:disabled': { backgroundColor: tokens.colorNeutralBackgroundDisabled, color: tokens.colorNeutralForegroundDisabled, ...shorthands.borderColor(tokens.colorNeutralStrokeDisabled) },
    '&:disabled:hover': { backgroundColor: tokens.colorNeutralBackgroundDisabled, color: tokens.colorNeutralForegroundDisabled, ...shorthands.borderColor(tokens.colorNeutralStrokeDisabled) },
  },
})
/**
 * EXPAND ALL / COLLAPSE ALL, one look on every queue (owner, 2026-09-15: "the
 * fonts/weights of the Expand all buttons are not consistent across all steps
 * in the pipeline. Whether you go with the lighter version or the darker
 * version, make these consistent, and be consistent between Select all and
 * Expand all, too").
 *
 * Three copies had drifted: `InvoiceQueue`'s own toolbar button was
 * `size="small"` (12px) while the per-company group headers on Export Control,
 * the approval screens and Closing Confirmation were the default medium (14px).
 *
 * THE LIGHTER READING WINS, for both halves of the instruction. Medium size, so
 * it matches the Approve / Deny / Export buttons it now sits beside — and
 * `fontWeightRegular`, so it matches the **Select all** checkbox label across
 * the same row, which Fluent renders at 14px regular. A subtle button's default
 * is semibold, which put two different weights in one toolbar saying two
 * equally unimportant things. The emphasis in that row belongs to the coloured
 * action buttons, not to the two controls that only change what is on screen.
 */
export const useQueueToolbarButtonStyles = makeStyles({
  subtle: { fontWeight: tokens.fontWeightRegular },
})
